import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Download,
  Eye,
  FileText,
  ListChecks,
  LogOut,
  ShieldCheck,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthShell from '../../components/auth/AuthShell';
import useAuthStore from '../../store/authStore';
import { employerAPI } from '../../services/api';
import { getUploadedDocument, hasAcceptedUpload } from './kycDocumentState';

const statusConfig = {
  verified: {
    label: 'Verified',
    detail: 'Employer verification is complete.',
    icon: CheckCircle2,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700'
  },
  under_review: {
    label: 'Under review',
    detail: 'Manual verification usually takes up to 24 hours and can be completed sooner.',
    icon: ShieldCheck,
    className: 'border-amber-200 bg-amber-50 text-amber-700'
  },
  in_review: {
    label: 'Under review',
    detail: 'Manual verification usually takes up to 24 hours and can be completed sooner.',
    icon: ShieldCheck,
    className: 'border-amber-200 bg-amber-50 text-amber-700'
  },
  rejected: {
    label: 'Action required',
    detail: 'Review the rejected document and upload a replacement.',
    icon: AlertTriangle,
    className: 'border-red-200 bg-red-50 text-red-700'
  },
  pending_documents: {
    label: 'Documents required',
    detail: 'Upload the required documents to request review.',
    icon: AlertCircle,
    className: 'border-sky-200 bg-sky-50 text-sky-700'
  },
  pending: {
    label: 'Documents required',
    detail: 'Upload the required documents to request review.',
    icon: AlertCircle,
    className: 'border-sky-200 bg-sky-50 text-sky-700'
  }
};

const documentStatusClasses = {
  verified: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  under_review: 'border-amber-200 bg-amber-50 text-amber-700',
  in_review: 'border-amber-200 bg-amber-50 text-amber-700',
  pending: 'border-sky-200 bg-sky-50 text-sky-700',
  rejected: 'border-red-200 bg-red-50 text-red-700'
};

const waitingQuotes = [
  {
    quote: 'The strongest hiring systems start with trust before they start with speed.',
    source: 'Sanspath employer verification'
  },
  {
    quote: 'Sanspath is built for serious employer relationships, clearer candidate signals, and decisions teams can stand behind.',
    source: 'Sanspath'
  },
  {
    quote: 'A verified employer profile helps candidates engage with confidence and helps hiring teams move with precision.',
    source: 'Sanspath employer network'
  }
];

const humanize = (value = '') =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeSides = (sides) => {
  if (!Array.isArray(sides) || !sides.length || sides.includes('single')) return ['single'];
  return ['front', 'back'].filter((side) => sides.includes(side));
};

const clampCount = (value, minimum = 1, maximum = 1) => {
  const parsed = Number.parseInt(value, 10);
  const safeMinimum = Math.max(Number(minimum) || 1, 1);
  const safeMaximum = Math.max(Number(maximum) || safeMinimum, safeMinimum);
  if (!Number.isFinite(parsed)) return safeMinimum;
  return Math.min(Math.max(parsed, safeMinimum), safeMaximum);
};

const requirementTitle = (requirement = {}) => requirement.slotLabel || requirement.display_name || requirement.displayName || humanize(requirement.document_type || requirement.documentType);

const buildRequirementSlots = (requirements = [], instanceCounts = {}) => requirements.flatMap((requirement) => {
  const subjectType = requirement.subject_type || requirement.subjectType || 'company';
  const allowMultiple = Boolean(requirement.allow_multiple || requirement.allowMultiple);
  const minimumInstances = Number(requirement.minimum_instances || requirement.minimumInstances || 1);
  const maximumInstances = Number(requirement.maximum_instances || requirement.maximumInstances || minimumInstances || 1);
  const instanceCount = subjectType === 'company' || !allowMultiple
    ? 1
    : clampCount(instanceCounts[requirement.id], minimumInstances, maximumInstances);
  const requiredSides = normalizeSides(requirement.required_sides || requirement.requiredSides);
  const displayName = requirement.display_name || requirement.displayName || humanize(requirement.document_type || requirement.documentType);
  const instanceLabel = requirement.instance_label || requirement.instanceLabel || humanize(subjectType).replace(/s$/, '');

  const slots = [];
  for (let subjectIndex = 1; subjectIndex <= instanceCount; subjectIndex += 1) {
    requiredSides.forEach((documentSide) => {
      const personLabel = instanceCount > 1 ? `${instanceLabel} ${subjectIndex}` : instanceLabel;
      const sideLabel = documentSide === 'single' ? '' : ` ${humanize(documentSide)}`;
      slots.push({
        ...requirement,
        id: `${requirement.id}:${subjectType}:${subjectIndex}:${documentSide}`,
        requirementId: requirement.id,
        slotKey: `${requirement.id}:${subjectType}:${subjectIndex}:${documentSide}`,
        slotLabel: `${displayName}${instanceCount > 1 ? ` - ${personLabel}` : ''}${sideLabel}`,
        display_name: displayName,
        subjectType,
        subjectIndex,
        documentSide,
        instanceLabel,
        personLabel,
        required_sides: requiredSides
      });
    });
  }

  return slots;
});

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const openBlob = (blob, filename, openInNewTab = false) => {
  const url = URL.createObjectURL(blob);

  if (openInNewTab) {
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

function DocumentStepRail({ requirements, currentDocuments, activeRequirementId, showReview, onSelect, onReview }) {
  const activeStepRef = useRef(null);

  useEffect(() => {
    if (!activeStepRef.current) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    activeStepRef.current.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }, [activeRequirementId, showReview]);

  return (
    <nav aria-label="Verification steps" className="mb-6 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
      <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth pb-1">
        {requirements.map((requirement, index) => {
          const document = getUploadedDocument(currentDocuments, requirement);
          const accepted = hasAcceptedUpload(currentDocuments, requirement);
          const rejected = document?.verificationStatus === 'rejected';
          const active = !showReview && activeRequirementId === requirement.id;
          const Icon = accepted ? CheckCircle2 : rejected ? AlertTriangle : FileText;
          const stateLabel = accepted ? 'Complete' : rejected ? 'Action required' : active ? 'Current' : 'Required';

          return (
            <button
              key={requirement.id}
              ref={active ? activeStepRef : null}
              type="button"
              onClick={() => onSelect(requirement)}
              aria-current={active ? 'step' : undefined}
              aria-label={`${requirementTitle(requirement)}. ${stateLabel}`}
              className={`flex min-w-[172px] snap-start items-center gap-3 rounded-lg border px-3 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 ${
                active
                  ? 'border-sky-200 bg-sky-50 text-sky-800 shadow-sm'
                  : accepted
                    ? 'border-emerald-100 bg-emerald-50/70 text-emerald-800 hover:bg-emerald-50'
                    : rejected
                      ? 'border-red-100 bg-red-50/70 text-red-800 hover:bg-red-50'
                      : 'border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                accepted ? 'bg-emerald-100 text-emerald-700' : rejected ? 'bg-red-100 text-red-700' : active ? 'bg-white text-sky-700' : 'bg-white text-slate-500'
              }`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.14em] opacity-70">Step {index + 1} - {stateLabel}</span>
                <span className="mt-0.5 block truncate text-sm font-semibold">{requirementTitle(requirement)}</span>
              </span>
            </button>
          );
        })}
        <button
          ref={showReview ? activeStepRef : null}
          type="button"
          onClick={onReview}
          aria-current={showReview ? 'step' : undefined}
          className={`flex min-w-[150px] snap-start items-center gap-3 rounded-lg border px-3 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 ${
            showReview ? 'border-slate-300 bg-slate-900 text-white shadow-sm' : 'border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${showReview ? 'bg-white/10 text-white' : 'bg-white text-slate-500'}`}>
            <ListChecks className="h-4 w-4" />
          </span>
          <span>
            <span className="block text-[11px] font-bold uppercase tracking-[0.14em] opacity-70">Final</span>
            <span className="mt-0.5 block text-sm font-semibold">Review</span>
          </span>
        </button>
      </div>
    </nav>
  );
}

function KYCVerificationPage() {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    updateKycStatus,
    verificationStatus,
    kycDocuments
  } = useAuthStore();

  const [kycStatus, setKycStatus] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [requirementsData, setRequirementsData] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [instanceCounts, setInstanceCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingIds, setUploadingIds] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [replacementIds, setReplacementIds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [activeRequirementId, setActiveRequirementId] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [stepTransition, setStepTransition] = useState({ active: false, label: '' });
  const stepTransitionTimerRef = useRef(null);

  const fetchKycStatus = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const data = await employerAPI.getKycStatus();
      const nextDocuments = data.documents || [];
      setKycStatus(data);
      setUploadedDocuments(nextDocuments);

      const region = data.region;
      if (!region) {
        throw new Error('Your company verification region is missing. Contact support before uploading documents.');
      }

      const requirementData = await employerAPI.getKycRequirements(region);
      const nextRequirements = requirementData.requirements || [];
      const defaultInstanceCounts = {};
      nextRequirements.forEach((requirement) => {
        defaultInstanceCounts[requirement.id] = Number(requirement.minimum_instances || requirement.minimumInstances || 1);
      });
      setInstanceCounts((current) => {
        const nextCounts = { ...current };
        nextRequirements.forEach((requirement) => {
          if (nextCounts[requirement.id]) return;
          nextCounts[requirement.id] = Number(requirement.minimum_instances || requirement.minimumInstances || 1);
        });
        return nextCounts;
      });
      const nextSlots = buildRequirementSlots(nextRequirements, defaultInstanceCounts);
      const required = nextSlots.filter((requirement) => requirement.is_required);
      const firstRequiredAttention = required.find((requirement) => !hasAcceptedUpload(nextDocuments, requirement));
      const firstRejected = nextSlots.find((requirement) => (
        getUploadedDocument(nextDocuments, requirement)?.verificationStatus === 'rejected'
      ));
      const firstRejectedAttention = firstRejected || firstRequiredAttention;

      setRequirements(nextRequirements);
      setRequirementsData(requirementData);
      setActiveRequirementId((currentId) => {
        if (data.verificationStatus === 'rejected' && firstRejectedAttention) {
          return firstRejectedAttention.id;
        }
        if (currentId && nextSlots.some((requirement) => requirement.id === currentId)) {
          return currentId;
        }
        return firstRequiredAttention?.id || required[0]?.id || nextRequirements[0]?.id || null;
      });
      if (data.verificationStatus === 'rejected' && firstRejectedAttention) {
        setShowReview(false);
      } else if (!firstRequiredAttention) {
        setShowReview(true);
      }

      return {
        data,
        documents: nextDocuments,
        requirements: nextRequirements
      };
    } catch (requestError) {
      const message = requestError?.message || 'Failed to load employer verification.';
      if (!silent) {
        setError(message);
        toast.error(message);
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    fetchKycStatus();
  }, [fetchKycStatus, isAuthenticated]);

  const currentStatus = kycStatus?.verificationStatus || verificationStatus || 'pending_documents';
  const isVerified = currentStatus === 'verified';
  const reviewLocked = ['under_review', 'in_review'].includes(currentStatus);
  const currentStatusConfig = statusConfig[currentStatus] || statusConfig.pending_documents;
  const StatusIcon = currentStatusConfig.icon;
  const currentDocuments = useMemo(
    () => (uploadedDocuments.length ? uploadedDocuments : (kycDocuments || [])),
    [uploadedDocuments, kycDocuments]
  );
  const orderedRequirements = useMemo(() => {
    const slots = buildRequirementSlots(requirements, instanceCounts);
    return [
      ...slots.filter((requirement) => requirement.is_required),
      ...slots.filter((requirement) => !requirement.is_required)
    ];
  }, [instanceCounts, requirements]);

  const requiredRequirements = useMemo(
    () => orderedRequirements.filter((requirement) => requirement.is_required),
    [orderedRequirements]
  );

  const activeRequirement = orderedRequirements.find((requirement) => requirement.id === activeRequirementId) ||
    requiredRequirements[0] ||
    orderedRequirements[0];
  const activeDocument = getUploadedDocument(currentDocuments, activeRequirement);

  const uploadedRequiredCount = useMemo(() => (
    requiredRequirements.filter((requirement) => hasAcceptedUpload(currentDocuments, requirement)).length
  ), [currentDocuments, requiredRequirements]);

  const allRequiredUploaded = requiredRequirements.length > 0 &&
    uploadedRequiredCount >= requiredRequirements.length;
  const hasActiveUploads = Object.values(uploadingIds).some(Boolean);
  const submissionReady = kycStatus?.canSubmitForReview === true || allRequiredUploaded;
  const canAttemptSubmit =
    !hasActiveUploads &&
    !reviewLocked &&
    !isVerified;
  const totalSteps = requiredRequirements.length + 1;
  const activeRequiredIndex = requiredRequirements.findIndex((requirement) => requirement.id === activeRequirement?.id);
  const currentStep = showReview ? totalSteps : Math.max(activeRequiredIndex + 1, 1);
  const documentProgress = requiredRequirements.length
    ? Math.round((uploadedRequiredCount / requiredRequirements.length) * 100)
    : 0;
  const activeQuote = waitingQuotes[quoteIndex % waitingQuotes.length];
  const documentFlowKey = showReview ? 'review' : activeRequirement?.id || 'document';

  useEffect(() => {
    if (!isAuthenticated || !reviewLocked) return undefined;

    const pollInterval = window.setInterval(() => {
      fetchKycStatus({ silent: true });
    }, 15000);

    return () => window.clearInterval(pollInterval);
  }, [fetchKycStatus, isAuthenticated, reviewLocked]);

  useEffect(() => {
    if (!reviewLocked) return undefined;

    const quoteInterval = window.setInterval(() => {
      setQuoteIndex((previous) => previous + 1);
    }, 8000);

    return () => window.clearInterval(quoteInterval);
  }, [reviewLocked]);

  useEffect(() => {
    if (!isVerified) return;

    let cancelled = false;
    const enterDashboard = async () => {
      try {
        await updateKycStatus();
      } catch (statusError) {
        console.warn('Failed to refresh KYC status before dashboard redirect:', statusError);
      }

      if (!cancelled) {
        toast.success('Verification complete. Opening your dashboard.');
        navigate('/dashboard', { replace: true });
      }
    };

    enterDashboard();
    return () => {
      cancelled = true;
    };
  }, [isVerified, navigate, updateKycStatus]);

  const completeStepTransition = useCallback(() => {
    if (stepTransitionTimerRef.current) {
      window.clearTimeout(stepTransitionTimerRef.current);
    }
    stepTransitionTimerRef.current = window.setTimeout(() => {
      setStepTransition({ active: false, label: '' });
      stepTransitionTimerRef.current = null;
    }, 280);
  }, []);

  useEffect(() => () => {
    if (stepTransitionTimerRef.current) {
      window.clearTimeout(stepTransitionTimerRef.current);
    }
  }, []);

  const moveToRequirement = (requirement) => {
    setReplacementIds({});
    setActiveRequirementId(requirement.id);
    setShowReview(false);
  };

  const advanceAfterUpload = (result) => {
    if (!result) {
      completeStepTransition();
      return;
    }
    const required = buildRequirementSlots(result.requirements, instanceCounts).filter((requirement) => requirement.is_required);
    const nextRequired = required.find((requirement) => !hasAcceptedUpload(result.documents, requirement));

    if (nextRequired) {
      moveToRequirement(nextRequired);
      completeStepTransition();
      return;
    }

    setShowReview(true);
    completeStepTransition();
  };

  const handleFileSelect = (requirement, files) => {
    const file = files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB.');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Upload a PDF, JPG, PNG, DOC, or DOCX file.');
      return;
    }

    setSelectedFiles((previous) => ({ ...previous, [requirement.id]: file }));
    uploadDocument(requirement, file);
  };

  const uploadDocument = async (requirement, selectedFile) => {
    const file = selectedFile || selectedFiles[requirement.id];
    if (!file) {
      toast.error('Choose a file first.');
      return;
    }

    try {
      if (!kycStatus?.region) {
        throw new Error('Your company verification region is missing. Refresh the page or contact support.');
      }

      setUploadingIds((previous) => ({ ...previous, [requirement.id]: true }));
      setUploadErrors((previous) => ({ ...previous, [requirement.id]: '' }));
      await employerAPI.uploadKycDocuments(file, requirement.requirementId || requirement.id, {
        region: kycStatus.region,
        subjectType: requirement.subjectType,
        subjectIndex: requirement.subjectIndex,
        documentSide: requirement.documentSide,
        instanceKey: requirement.slotKey
      });
      setStepTransition({ active: true, label: 'Saving document and preparing the next step' });
      toast.success(`${requirementTitle(requirement)} uploaded.`);
      setSelectedFiles((previous) => ({ ...previous, [requirement.id]: null }));
      setReplacementIds((previous) => ({ ...previous, [requirement.id]: false }));
      try {
        await updateKycStatus();
      } catch (statusError) {
        console.warn('Document saved, but the account status refresh failed:', statusError);
      }
      const refreshed = await fetchKycStatus({ silent: true });
      if (!refreshed) {
        setStepTransition({ active: false, label: '' });
        toast('Document saved. Refresh the page to load your updated checklist.');
        return;
      }
      advanceAfterUpload(refreshed);
    } catch (uploadError) {
      const message = uploadError?.message || 'Document upload failed.';
      setUploadErrors((previous) => ({ ...previous, [requirement.id]: message }));
      setStepTransition({ active: false, label: '' });
      toast.error(message);
    } finally {
      setUploadingIds((previous) => ({ ...previous, [requirement.id]: false }));
    }
  };

  const submitForVerification = async () => {
    try {
      setSubmitting(true);
      if (!kycStatus?.region) {
        throw new Error('Your company verification region is missing. Refresh the page or contact support.');
      }

      const latest = await fetchKycStatus({ silent: true });
      if (!latest) {
        throw new Error('Could not confirm your latest document status. Check your connection and try again.');
      }

      const latestStatus = latest.data?.verificationStatus;
      if (['under_review', 'in_review'].includes(latestStatus)) {
        toast.success('Your documents are already under review.');
        return;
      }

      const latestRequired = buildRequirementSlots(latest.requirements, instanceCounts)
        .filter((requirement) => requirement.is_required);
      const firstBlockedRequirement = latestRequired.find((requirement) => (
        !hasAcceptedUpload(latest.documents, requirement)
      ));
      const serverReady = latest.data?.canSubmitForReview;
      const readyForSubmission = serverReady === true ||
        (serverReady === undefined && latestRequired.length > 0 && !firstBlockedRequirement);

      if (!readyForSubmission) {
        if (firstBlockedRequirement) moveToRequirement(firstBlockedRequirement);
        const rejected = latest.data?.kycCompletion?.rejected_documents || [];
        const missing = latest.data?.kycCompletion?.missing_uploads || [];
        const outstanding = [...rejected, ...missing].filter(Boolean);
        throw new Error(outstanding.length
          ? `Update the outstanding document${outstanding.length === 1 ? '' : 's'}: ${outstanding.join(', ')}.`
          : 'Your latest documents are still being saved. Wait a moment and try again.');
      }

      const response = await employerAPI.submitKycForVerification({ region: latest.data.region || kycStatus.region });
      setKycStatus((current) => ({
        ...current,
        verificationStatus: response?.status || 'under_review',
        canSubmitForReview: false,
        reviewLocked: true
      }));
      toast.success('Submitted for verification.');
      try {
        await updateKycStatus();
      } catch (statusError) {
        console.warn('KYC submitted, but the account status refresh failed:', statusError);
      }
      await fetchKycStatus({ silent: true });
    } catch (submitError) {
      toast.error(submitError?.message || 'Could not submit verification.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = async (document) => {
    try {
      const blob = await employerAPI.previewKycDocument(document.id);
      openBlob(blob, document.originalFilename || 'document', true);
    } catch (previewError) {
      toast.error(previewError?.message || 'Preview failed.');
    }
  };

  const handleDownload = async (document) => {
    try {
      const blob = await employerAPI.downloadKycDocument(document.id);
      openBlob(blob, document.originalFilename || 'document');
    } catch (downloadError) {
      toast.error(downloadError?.message || 'Download failed.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const renderUploader = (requirement, replacement = false) => {
    const selectedFile = selectedFiles[requirement.id];
    const isUploading = Boolean(uploadingIds[requirement.id]);
    const uploadError = uploadErrors[requirement.id];

    return (
      <div
        className={`rounded-xl border border-dashed p-4 transition ${replacement ? 'border-red-300 bg-red-50/70' : 'border-sky-200 bg-sky-50/50 hover:border-sky-400 hover:bg-sky-50'}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!isUploading) handleFileSelect(requirement, event.dataTransfer.files);
        }}
      >
        {replacement && (
          <p className="mb-3 text-sm font-medium text-red-700">
            Upload a replacement document. The new file saves as soon as you choose it.
          </p>
        )}
        <label
          htmlFor={`document-${requirement.id}`}
          className={`flex items-center gap-3 rounded-lg bg-white px-4 py-4 text-sm ring-1 ring-inset ring-slate-200 transition hover:ring-sky-300 ${isUploading ? 'cursor-wait text-slate-500' : 'cursor-pointer text-slate-600'}`}
        >
          {isUploading
            ? <CircleDashed className="h-5 w-5 shrink-0 animate-spin text-sky-600" />
            : <Upload className="h-5 w-5 shrink-0 text-sky-600" />}
          <span className="min-w-0">
            <span className="block truncate font-medium text-slate-800">
              {isUploading ? `Uploading ${selectedFile?.name || 'document'}...` : 'Choose a document or drop it here'}
            </span>
            <span className="mt-1 block text-xs leading-5 text-slate-500">
              PDF, JPG, PNG, DOC, or DOCX up to 10MB. Upload starts immediately.
            </span>
          </span>
        </label>
        <input
          id={`document-${requirement.id}`}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          disabled={isUploading}
          onChange={(event) => {
            handleFileSelect(requirement, event.target.files);
            event.target.value = '';
          }}
        />
        {uploadError && <p className="mt-3 text-sm text-red-600">{uploadError}</p>}
      </div>
    );
  };

  const renderDocumentFile = (document) => {
    const statusClass = documentStatusClasses[document.verificationStatus] || documentStatusClasses.pending;

    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700 shadow-sm ring-1 ring-slate-200">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">
                {document.originalFilename || document.displayName || humanize(document.documentType)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {document.createdAt ? `Uploaded ${new Date(document.createdAt).toLocaleDateString()}` : 'Uploaded'}
                {document.fileSizeBytes ? ` - ${formatBytes(document.fileSizeBytes)}` : ''}
              </p>
              {((document.subjectType && document.subjectType !== 'company') || document.documentSide !== 'single') && (
                <p className="mt-1 text-xs text-slate-500">
                  {document.subjectName || `${humanize(document.subjectType || 'company')} ${document.subjectIndex || 1}`}
                  {document.documentSide && document.documentSide !== 'single' ? ` - ${humanize(document.documentSide)}` : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass}`}>
              {humanize(document.verificationStatus || 'pending')}
            </span>
            <button
              type="button"
              onClick={() => handlePreview(document)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
              aria-label="Preview document"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleDownload(document)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
              aria-label="Download document"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
        {document.rejectionReason && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm leading-6 text-red-700">
            {document.rejectionReason}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <AuthShell title="Checking company verification" subtitle="Loading your saved document progress.">
        <div className="flex items-center gap-3 rounded-md border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-slate-600">
          <CircleDashed className="h-4 w-4 animate-spin text-sky-600" />
          Loading verification progress
        </div>
      </AuthShell>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthShell title="Sign in required" subtitle="Use your employer account to complete company verification.">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="inline-flex w-full items-center justify-center rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-sky-700"
        >
          Go to sign in
        </button>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell title="Unable to load verification" subtitle={error}>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => fetchKycStatus()}
            className="flex-1 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-sky-700"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      </AuthShell>
    );
  }

  if (reviewLocked) {
    return (
      <AuthShell
        eyebrow="Employer verification"
        title="Your documents are in review"
        subtitle="Your progress is saved. We will open your dashboard automatically as soon as verification is complete."
      >
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${currentStatusConfig.className}`}>
          <StatusIcon className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">{currentStatusConfig.label}</p>
            <p className="mt-1 leading-5">{currentStatusConfig.detail}</p>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-amber-700">
            <Clock3 className="h-4 w-4" />
            Review window: up to 24 hours, often less
          </div>
          <blockquote className="text-sm leading-6 text-slate-700">"{activeQuote.quote}"</blockquote>
          <p className="mt-3 text-xs font-medium text-amber-700">{activeQuote.source}</p>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
          <CircleDashed className="h-3.5 w-3.5 animate-spin text-sky-600" />
          Auto-checking verification status
        </div>

        <div className="mt-6 border-t border-slate-200 pt-5 text-center">
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#0b1f3a]"
          >
            <LogOut className="h-4 w-4" />
            Sign out and continue later
          </button>
        </div>
      </AuthShell>
    );
  }

  if (!requirements.length || !activeRequirement) {
    return (
      <AuthShell title="No verification requirements found" subtitle="Try refreshing the page. Contact support if this continues.">
        <button
          type="button"
          onClick={() => fetchKycStatus()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-sky-700"
        >
          <ShieldCheck className="h-4 w-4" />
          Check again
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow={activeRequiredIndex >= 0
        ? `Company verification - Step ${currentStep} of ${totalSteps}`
        : 'Company verification - Optional evidence'}
      title={showReview ? 'Review your company documents' : requirementTitle(activeRequirement)}
      subtitle={showReview
        ? 'Confirm the saved files below, add optional evidence if useful, then send your company profile for verification.'
        : activeRequirement.upload_instructions || activeRequirement.uploadInstructions || activeRequirement.document_description || 'Upload the requested company evidence to continue.'}
      maxWidth="max-w-3xl"
    >
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-500">
          <span>{uploadedRequiredCount} of {requiredRequirements.length} required documents saved</span>
          <span>{documentProgress}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all"
            style={{ width: `${documentProgress}%` }}
          />
        </div>
      </div>

      <DocumentStepRail
        requirements={requiredRequirements}
        currentDocuments={currentDocuments}
        activeRequirementId={activeRequirement?.id}
        showReview={showReview}
        onSelect={moveToRequirement}
        onReview={() => setShowReview(true)}
      />

      {currentStatus === 'rejected' && kycStatus?.rejectionReason && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
          <span className="font-medium">Action required:</span> {kycStatus.rejectionReason}
        </div>
      )}

      <div className="relative overflow-hidden rounded-lg" aria-busy={stepTransition.active}>
        {stepTransition.active && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-sky-100 bg-white/90 px-6 text-center backdrop-blur-sm" role="status" aria-live="polite">
            <div>
              <CircleDashed className="mx-auto h-8 w-8 animate-spin text-sky-600" />
              <p className="mt-3 text-sm font-semibold text-[#0b1f3a]">{stepTransition.label || 'Preparing next step'}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Your file is saved before we move the workflow forward.</p>
            </div>
          </div>
        )}

        <div key={documentFlowKey} className={`transition-all duration-300 ease-out ${stepTransition.active ? 'scale-[0.99] opacity-30' : 'scale-100 opacity-100'}`}>
          {!showReview ? (
            <div>
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${activeRequirement.is_required ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-500'}`}>
              {activeRequirement.is_required ? 'Required document' : 'Optional document'}
            </span>
            {activeRequirement.documentSide && activeRequirement.documentSide !== 'single' && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {humanize(activeRequirement.documentSide)} side
              </span>
            )}
            <span className="text-xs text-slate-500">
              Files save immediately after selection
            </span>
          </div>

          {(activeRequirement.allow_multiple || activeRequirement.allowMultiple) && activeRequirement.subjectType !== 'company' && (
            <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="block text-sm font-medium text-[#0b1f3a]">
                How many {humanize(activeRequirement.subjectType).toLowerCase()} need this document?
                <input
                  type="number"
                  min={activeRequirement.minimum_instances || activeRequirement.minimumInstances || 1}
                  max={activeRequirement.maximum_instances || activeRequirement.maximumInstances || 50}
                  value={instanceCounts[activeRequirement.requirementId] || activeRequirement.minimum_instances || activeRequirement.minimumInstances || 1}
                  onChange={(event) => {
                    const count = clampCount(
                      event.target.value,
                      activeRequirement.minimum_instances || activeRequirement.minimumInstances || 1,
                      activeRequirement.maximum_instances || activeRequirement.maximumInstances || 50
                    );
                    setInstanceCounts((previous) => ({ ...previous, [activeRequirement.requirementId]: count }));
                  }}
                  className="mt-2 w-28 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                We will create one upload step for each person and each required side of the document.
              </p>
            </div>
          )}

          {activeDocument ? (
            <div className="space-y-4">
              {renderDocumentFile(activeDocument)}
              {activeDocument.verificationStatus === 'rejected' ? (
                renderUploader(activeRequirement, true)
              ) : replacementIds[activeRequirement.id] ? (
                <div>
                  {renderUploader(activeRequirement, true)}
                  <button
                    type="button"
                    onClick={() => setReplacementIds((previous) => ({ ...previous, [activeRequirement.id]: false }))}
                    className="mt-3 text-sm font-medium text-slate-500 transition hover:text-[#0b1f3a]"
                  >
                    Cancel replacement
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setReplacementIds((previous) => ({ ...previous, [activeRequirement.id]: true }))}
                    className="text-left text-sm font-medium text-sky-700 transition hover:text-sky-800"
                  >
                    Replace this document
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReview(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700"
                  >
                    Continue to review
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            renderUploader(activeRequirement)
          )}

          <div className="mt-6 border-t border-slate-200 pt-5">
            <button
              type="button"
              onClick={() => setShowReview(true)}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#0b1f3a]"
            >
              <ListChecks className="h-4 w-4" />
              Open document checklist
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="space-y-3">
            {orderedRequirements.map((requirement) => {
              const document = getUploadedDocument(currentDocuments, requirement);
              const accepted = hasAcceptedUpload(currentDocuments, requirement);
              const statusClass = documentStatusClasses[document?.verificationStatus] || 'border-slate-200 bg-slate-50 text-slate-500';

              return (
                <div key={requirement.id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accepted ? 'bg-emerald-50 text-emerald-600' : document ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-400'}`}>
                      {accepted ? <CheckCircle2 className="h-5 w-5" /> : document ? <AlertTriangle className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium text-slate-900">{requirementTitle(requirement)}</p>
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          {requirement.is_required ? 'Required' : 'Optional'}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-xs text-slate-500">
                        {document?.originalFilename || (requirement.is_required ? 'Upload required' : 'Not added')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass}`}>
                      {document ? humanize(document.verificationStatus || 'pending') : 'Not uploaded'}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveToRequirement(requirement)}
                      className="text-sm font-medium text-sky-700 transition hover:text-sky-800"
                    >
                      {document ? 'Review' : 'Upload'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl border border-sky-100 bg-sky-50/70 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
              <div>
                <p className="text-sm font-medium text-[#0b1f3a]">Ready for manual verification</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Each uploaded file is already saved. Submit when every required document is present.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={submitForVerification}
            disabled={!canAttemptSubmit || submitting}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting && <CircleDashed className="h-4 w-4 animate-spin" />}
            {submitting ? 'Submitting...' : submissionReady ? 'Send for verification' : 'Check documents and submit'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>

          {!submissionReady && canAttemptSubmit && (
            <p className="mt-3 text-center text-sm leading-6 text-slate-500">
              We will check the latest saved documents and take you directly to anything that still needs attention.
            </p>
          )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5 text-center">
        <p className="mb-3 text-xs leading-5 text-slate-500">
          {user?.companyName || 'Your company'} - {requirementsData?.regionName || kycStatus?.region || 'Assigned region'}
        </p>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#0b1f3a]"
        >
          <LogOut className="h-4 w-4" />
          Sign out and continue later
        </button>
      </div>
    </AuthShell>
  );
}

export default KYCVerificationPage;
